import { dispatchDetectedAppVersion, getAppVersionFromResponse } from './utils/app-version';

const axios = require('axios');
const CancelToken = axios.CancelToken;

const getCookie = name => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        let cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = $.trim(cookies[i]);
            if (cookie.substring(0, name.length + 1) === name + '=') {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
};
const getCsrfToken = () => {
    const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]');
    return tokenInput ? tokenInput.value : null;
};
const currentSite = `${current_scheme}://${current_site}`;

const csrftoken = getCsrfToken();
const sessionid = getCookie('sessionid');

let axiosInitHeaders = {
    accept: 'application/json',
};
if (csrftoken) {
    axiosInitHeaders['X-CSRFToken'] = csrftoken;
}

const Axios = axios.create({
    headers: axiosInitHeaders,
});

Axios.defaults.withCredentials = true;

Axios.interceptors.request.use(
    config => {
        const userLang =
            getCookie('django_language') || localStorage.getItem('user_lang') || document.documentElement.lang;

        if (userLang) {
            config.headers['Accept-Language'] = userLang;
        }

        if (!config.headers['X-CSRFToken']) {
            config.headers['X-CSRFToken'] = getCsrfToken();
        }
        return config;
    },
    error => {
        Promise.reject(error);
    }
);

Axios.interceptors.response.use(
    response => {
        dispatchDetectedAppVersion(getAppVersionFromResponse(response));
        return response;
    },
    async function (error) {
        if (error) {
            const { config, response } = error;
            const originalRequest = config;
            dispatchDetectedAppVersion(getAppVersionFromResponse(response));
            if (axios.isCancel(error)) {
                return new Promise(() => {});
            }
            if (response && response.status === 401) {
                if (error.response.request.responseURL.includes('//quotes.')) {
                    window.location.reload();
                } else {
                    window.location.href = '/login/';
                    return Promise.reject(error);
                }
            }
            if (
                response &&
                response.status === 403 &&
                response.data.hasOwnProperty('detail') &&
                response.data.detail.includes('CSRF')
            ) {
                window.location.reload();
            }
            return Promise.reject(error);
        }
    }
);

let header = {};
const progress = {
    header: {
        'Content-Type': 'multipart/form-data',
    },
    onUploadProgress(progressEvent) {
        let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    },
};

const App = {
    getCookie(name) {
        return getCookie(name);
    },
    getCsrfToken() {
        return getCsrfToken();
    },
};

const User = {
    getUser(user_id) {
        return Axios.get('/users/' + user_id + '/');
    },
    getCurrentUser(data) {
        return Axios.get('/users/get_current/', data);
    },
    getRoles() {
        return Axios.get('/users/roles/');
    },
    getCustomerRole() {
        return Axios.get('/users/customer/role/');
    },
    create: userData => Axios.post(`/users/`, userData, header),

    update: userData => Axios.put('/users/' + userData.id + '/', userData, header),

    partialUpdate: userData => Axios.patch('/users/' + userData.id + '/', userData, header),

    deleteUser: userId => Axios.delete('/users/' + userId + '/delete/'),

    resetPassword: data => Axios.post('/users/' + data.id + '/password-reset/', data),

    activateUser(user_id) {
        return Axios.put(`/users/${user_id}/reactivate-user/`);
    },

    deactivateUser(user_id) {
        return Axios.put(`/users/${user_id}/deactivate-user/`);
    },

    search(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/users/search/?${get_params}`, header);
    },

    getAmiceUses(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/users/for-amice/?${get_params}`, header);
    },

    getDrivers(search) {
        return Axios.get('/users/drivers/?q=' + search);
    },

    getRegions(search) {
        return Axios.get('/users/regions/?q=' + search);
    },
    userAuth(data) {
        return Axios.post(`/users/auth/?next=${data.next}${data.urlHash}`, data);
    },

    verifyToken(data) {
        return Axios.post('/users/auth/confirm-key/', data);
    },
    getQRCode(data) {
        return Axios.post('/users/auth/get-qr-url/', data);
    },

    resetAuth(data) {
        return Axios.post('/users/auth/reset-auth/', data);
    },

    userLogin(data) {
        return Axios.post('/users/login/', data);
    },
    blockIP(data) {
        return Axios.post('/users/block-ip/', data);
    },
};

const UserGroup = {
    get(user_group_id) {
        return Axios.get('/user-groups/' + user_group_id + '/');
    },
    all() {
        return Axios.get('/user-groups/');
    },
    create(user_group) {
        return Axios.post('/user-groups/', user_group);
    },
    update(user_group) {
        return Axios.put('/user-groups/' + user_group.id + '/', user_group);
    },
    delete(user_group_id) {
        return Axios.delete('/user-groups/' + user_group_id + '/');
    },
};

const PermissionGroup = {
    get(permission_group_id) {
        return Axios.get('/users/permission-groups/' + permission_group_id + '/');
    },
    all() {
        return Axios.get('/users/permission-groups/');
    },
};

const Group = {
    get(group_id) {
        return Axios.get('/groups/' + group_id + '/');
    },
    create: groupData => Axios.post(`/groups/`, groupData, header),

    update: groupData => Axios.put('/groups/' + groupData.id + '/', groupData, header),
    getGroups(search) {
        return Axios.get('/groups/list/');
    },
};

const Address = {
    getAvailableCountries() {
        return Axios.get('/addresses/available-countries/');
    },
    completeAddress(zip_code, house_number, country, args) {
        return Axios.post('/addresses/complete/' + zip_code + '/' + house_number + '/' + country + '/', args);
    },
    searchAddress(country, context, precision) {
        return Axios.post(`/addresses/search/${country}/${context}/${precision}/`);
    },
    create(address) {
        return Axios.post('/addresses/save/', address);
    },
    update(address) {
        return Axios.put('/addresses/save/' + address.id + '/', address);
    },
    getAddress(address_id) {
        return Axios.get(`/address/${address_id}/`, header);
    },
    getAll() {
        return Axios.get('/addresses/datatable/', header);
    },
    list(search = '', props = '') {
        return Axios.get(`/addresses/list/?q=${search}&${props}`, header);
    },
    listPageRelation(filters = '', page = 1) {
        return Axios.get(`/addresses-relation/list/${page}/?${filters}`, header);
    },
    getAllPlaces() {
        return Axios.get('/addresses/places/', header);
    },
    requestAddressRelation(filters = '') {
        return Axios.get(`/addresses-relation/fetch-socket/?${filters}`, header);
    },
    getOpeningHoursByRelation(data) {
        return Axios.get(`/addresses-relation/opening-hours/${data.address_id}/${data.relation_id}/`, header);
    },
};

const Contact = {
    getContactsForRelation(relation_id, search, fields = '') {
        return Axios.get(`/relation/${relation_id}/contacts/?q=${search}&${fields}`);
    },
    getContact(contact_id) {
        return Axios.get('/contacts/' + contact_id + '/');
    },
    create(contact) {
        return Axios.post('/contacts/save/', contact, header);
    },
    update(contact) {
        return Axios.put(`/contacts/save/${contact.id}/`, contact);
    },
    delete(contact_id) {
        return Axios.delete('/contacts/delete/' + contact_id + '/');
    },
    existContact(data) {
        return Axios.get('/contacts/exists/', { params: data });
    },
    sendWasteReport(contact, data) {
        return Axios.post(`/contacts/${contact.id}/send-waste-report/`, data);
    },
};

const Tag = {
    getTags(search, scope) {
        // if scope === 1 then it`s CRM
        // if scope === 2 then it`s HRM
        // if scope === 3 then it`s Sales
        return Axios.get(`/tags/options/?q=${search}&scope=${scope}`);
    },
    create(tag) {
        return Axios.post('/tags/save/', tag, header);
    },
    update(tag) {
        return Axios.put(`/tags/save/${tag.id}/`, tag);
    },
    delete(tag) {
        return Axios.delete(`/tags/delete/${tag.id}/`);
    },
};

const Document = {
    download(url) {
        return Axios.get(url, { responseType: 'blob' });
    },
    get(id) {
        return Axios.get(`/document/${id}/`, header);
    },
    delete(id) {
        return Axios.delete(`/document/delete/${id}/`, header);
    },
    create(formData) {
        return Axios.post(`/document/create/`, formData, progress);
    },
    update(formData) {
        return Axios.put(`/document/update/${JSON.parse(formData.get('id'))}/`, formData, header);
    },
    update_name(document) {
        return Axios.put(`/document/update/name/${document.id}/`, document);
    },
};

const Kruispunt = {
    getKruispuntDenominations: function (query = '', type = 'establishment') {
        return Axios.get(`/kruispunt-denominations/?query=${query}&entityType=${type}`);
    },
    getKruispuntDenominationsForType: function (type, number) {
        return Axios.get(`/kruispunt-denominations/${type}/${number}/`);
    },
    getKruispuntEntity: function (type, number) {
        return Axios.get(`/kruispunt-entity/${type}/${number}/`);
    },
    getKruispuntEstablishmentsByEnterpriseNumber: function (number) {
        return Axios.get(`/kruispunt-establishments/${number}/`);
    },
    getKruispuntSBI: function (type, number) {
        return Axios.get(`/kruispunt-sbi/${type}/${number}/`);
    },
    getKruispuntAddress: function (type, number) {
        return Axios.get(`/kruispunt-address/${type}/${number}/`);
    },
};

const kvkNL = {
    getByCompanyName(search) {
        return Axios.get(`/kvk-nl/by-company-name/${search}/`);
    },
    getByKVK(kvk) {
        return Axios.get(`/kvk-nl/by-kvk/${kvk}/`);
    },
    getByEstNum(search) {
        return Axios.get(`/kvk-nl/by-establishment-number/${search}/`);
    },
    getFullCompanyInfo(type, kvk) {
        return Axios.get(`/kvk-nl/full-company-info/${type}/${kvk}/`);
    },
};

const handelsregisterDE = {
    search(query) {
        return Axios.get(`/handelsregister-de/search/${encodeURIComponent(query)}/`);
    },
    getCompany(query) {
        return Axios.get(`/handelsregister-de/company/${encodeURIComponent(query)}/`);
    },
};

const Relation = {
    canBePurged(id) {
        return Axios.get(`/relations/can-be-purged/${id}/`, header);
    },
    purge(id) {
        return Axios.delete(`/relations/purge/${id}/`, header);
    },
    forceUpdateToExact(id) {
        return Axios.post(
            '/relations/force-update-to-exact/',
            {
                id: id,
            },
            header
        );
    },
    forceUpdateByA2BRlnCode(id, a2b_rln_code) {
        return Axios.post(
            '/relations/force-update-to-exact-by-rln-code/',
            {
                id: id,
                a2b_rln_code: a2b_rln_code,
            },
            header
        );
    },
    create(relation) {
        return Axios.post('/relations/save/', relation, header);
    },
    update(relation) {
        return Axios.put(`/relations/save/${relation.id}/`, relation, header);
    },
    partial_update(relation) {
        return Axios.patch(`/relations/save/${relation.id}/`, relation, header);
    },
    delete_exact_connection(relation_id) {
        return Axios.post(`/relation/delete-exact-connection/${relation_id}/`, { headers: header });
    },
    getAccountManagers() {
        return Axios.get('/account-managers/', header);
    },
    validateVat(vat_number) {
        return Axios.get('/validate-vat/' + vat_number + '/', header);
    },
    getBicCode(iban_number) {
        return Axios.get('/validate-iban/' + iban_number + '/', header);
    },
    getCompanyInfoKVK(href) {
        return Axios.get(`/get-company-info-kvk${href}/`, header);
    },
    getSuggestionsKVK(query) {
        return Axios.get(`/get-suggestions-kvk/${query}/`, header);
    },
    searchRelations(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                if (typeof value == 'object' && !Array.isArray(value)) {
                    for (let v in value) {
                        get_params += `${key}[${v}]=${encodeURIComponent(value[v])}&`;
                    }
                } else {
                    get_params += `${key}=${encodeURIComponent(value)}&`;
                }
            }
        }
        return Axios.get(`/relations/search/?${get_params}`, header);
    },
    searchRelationTitles(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${encodeURIComponent(value)}`;
            }
        }
        return Axios.get(`/relations/titles/search/?${get_params}`, header);
    },
    getRelation(relation_id, fields = '', params = {}) {
        const queryParams = new URLSearchParams(params).toString();
        const separator = fields && queryParams ? '&' : '';
        return Axios.get(`/relations/${relation_id}/?${fields}${separator}${queryParams}`, header);
    },
    getOpenSanctions(relationId, permissionScope = 'crm') {
        return Axios.get(`/relations/${relationId}/opensanctions/`, {
            ...header,
            params: { permission_scope: permissionScope },
        });
    },
    checkOpenSanctions(relationId, permissionScope = 'crm') {
        return Axios.post(
            `/relations/${relationId}/opensanctions/check/?permission_scope=${encodeURIComponent(permissionScope)}`,
            {},
            header
        );
    },
    overrideOpenSanctions(relationId, reason, permissionScope = 'crm') {
        return Axios.post(
            `/relations/${relationId}/opensanctions/override/?permission_scope=${encodeURIComponent(permissionScope)}`,
            { reason },
            header
        );
    },
    getRelationAndParent(relation_id, fields = '') {
        return Axios.get(`/relations/${relation_id}/with-parents/?${fields}`, header);
    },
    getContacts(relation_id, fields = '') {
        return Axios.get(`/relations/${relation_id}/contacts/?${fields}`, header);
    },
    getRelationTags(relationId, fields = '') {
        return Axios.get(`/relations/${relationId}/tags/?${fields}`, header);
    },
    getDocuments(relation_id) {
        return Axios.get(`/relations/${relation_id}/documents/`, header);
    },
    getAddresses(relation_id) {
        return Axios.get(`/relations/${relation_id}/addresses/`, header);
    },
    getPricing(relation_id) {
        return Axios.get(`/relations/${relation_id}/pricing/`, header);
    },
    getTurnovers(relation_id) {
        return Axios.get(`/relations/${relation_id}/turnovers/`, header);
    },
    getInvoiceDebtor(relation_id, fields = '') {
        return Axios.get(`/relations/${relation_id}/invoice-debtor/?${fields}`, header);
    },
    getAlternativeDebtors(relation_id) {
        return Axios.get(`/relations/${relation_id}/alternative_debtors/`, header);
    },
    getDeactivationPreview(relation_id) {
        return Axios.get(`/relations/${relation_id}/deactivation-preview/`, header);
    },
    getCustomerReferences(relation_id) {
        return Axios.get(`/relations/${relation_id}/customer_references/`, header);
    },
    getPriceLists(relation_id) {
        return Axios.get(`/relations/${relation_id}/price_lists/`, header);
    },
    resolvePeppolAddressForData(params = {}) {
        return Axios.get('/relations/resolve_peppol_address/', { ...header, params });
    },
    getSBICodes(query) {
        return Axios.get(`/sbi/search/?q=${query}`, header);
    },
    checkSBICodes(data) {
        return Axios.post(`/sbi/check/`, data, header);
    },
    getPerTypes() {
        return Axios.get('/relations/per-type/', header);
    },
    getPerAccountManagers() {
        return Axios.get('/relations/per-account-manager/', header);
    },
    getPerCountries() {
        return Axios.get('/relations/per-country/', header);
    },
    getPerIndustry() {
        return Axios.get('/relations/per-industry/', header);
    },
    delete(relation_id) {
        return Axios.delete('/relations/delete/' + relation_id + '/', header);
    },
    getAmountOfOpenTasks(relation_id) {
        return Axios.get('/relation/' + relation_id + '/amount-of-open-tasks/', header);
    },
    getAmountOfOpenedOrders(relation_id) {
        return Axios.get('/relation/' + relation_id + '/amount-of-open-orders/', header);
    },
    getDisposalAddresses(relation_id) {
        return Axios.get('/relation/' + relation_id + '/disposal-addresses/', header);
    },
    getTags() {
        return Axios.get('/relations/tags/', header);
    },
    getInvoiceDebtors() {
        return Axios.get('/relations/invoice-debtors/', header);
    },
    getByType(typeName, search = '', full_list = false) {
        return Axios.get(`/relations/by-type/${typeName}/?search=${search}&full_list=${full_list}`, header);
    },
    getParents(search = '', full_list = false) {
        return Axios.get(`/relations/list-parents/?search=${search}&full_list=${full_list}`, header);
    },
    searchRelationInExact(query, field) {
        return Axios.get(`/relations/exact/?q=${query}`);
    },
    getDisposerForOutboundOrder(origin) {
        return Axios.get(`/relations/get-disposer-for-outbound-order/?origin=${origin.id}`, header);
    },
    getWasteGroups(relation_id) {
        return Axios.get(`/relations/${relation_id}/waste-groups/`);
    },
    getItemPrices(relation_id) {
        return Axios.get(`/relations/${relation_id}/item-prices/`);
    },
    getFinanceOverview(relation_id) {
        return Axios.get(`/relations/${relation_id}/finance-overview/`);
    },
    setExactId(relation_id, exact_id) {
        return Axios.post(`/relations/set-exact-id/`, { relation_id: relation_id, exact_id: exact_id });
    },
};

const ContactRelation = {
    getContactRelations(contact_id) {
        return Axios.get(`/contact-relations/${contact_id}/`, header);
    },
    create(contact_relation) {
        return Axios.post('/contact-relations/save/', contact_relation, header);
    },
    validateEmail(data) {
        return Axios.post('/contact-relations/validate-email/', data, header);
    },
    delete(contact_relation_id, replacement_contact_id = null) {
        let url = `/contact-relations/delete/${contact_relation_id}/`;
        if (replacement_contact_id) {
            url += `?replacement_contact_id=${replacement_contact_id}`;
        }
        return Axios.delete(url, header);
    },
};

const Establishment = {
    create(establishment) {
        return Axios.post('/app-settings/establishments/save/', establishment, header);
    },
    update(establishment) {
        return Axios.put('/app-settings/establishments/save/' + establishment.id + '/', establishment, header);
    },
    delete(id) {
        return Axios.delete('/app-settings/establishments/delete/' + id + '/', header);
    },
    partial_update(establishment) {
        return Axios.patch('/app-settings/establishments/save/' + establishment.id + '/', establishment, header);
    },
    getEstablishments(search = '', params = {}) {
        return Axios.get('/app-settings/establishments/options/', {
            params: {
                q: search,
                ...params,
            },
        });
    },
    getAllEstablishments(search, params = {}) {
        return Axios.get('/app-settings/all-establishments/', { params });
    },
    getActiveEstablishments(params = {}) {
        return Axios.get(`/app-settings/active-establishments/`, { params });
    },
    getEstablishment(id) {
        return Axios.get('/app-settings/establishments/' + id + '/', header);
    },
    getEstablishmentByRelation(relation_id) {
        return Axios.get('/app-settings/establishments/by-relation/' + relation_id + '/', header);
    },
};

const ProcessingMethod = {
    create(establishment) {
        return Axios.post('/app-settings/processing-method/save/', establishment, header);
    },
    update(establishment) {
        return Axios.put('/app-settings/processing-method/save/' + establishment.id + '/', establishment, header);
    },
    delete(id) {
        return Axios.delete('/app-settings/processing-method/delete/' + id + '/', header);
    },
    get(id) {
        return Axios.get('/app-settings/processing-method/' + id + '/');
    },
    getAll(search) {
        return Axios.get(`/app-settings/processing-methods/?search=${search}`);
    },
    fetch(queryString) {
        return Axios.get('/app-settings/processing-method/fetch/?' + queryString);
    },
};

const OutboundProcessingMethod = {
    create(establishment) {
        return Axios.post('/app-settings/outbound-processing-method/save/', establishment, header);
    },
    update(establishment) {
        return Axios.put(
            '/app-settings/outbound-processing-method/save/' + establishment.id + '/',
            establishment,
            header
        );
    },
    delete(id) {
        return Axios.delete('/app-settings/outbound-processing-method/delete/' + id + '/', header);
    },
    get(id) {
        return Axios.get('/app-settings/outbound-processing-method/' + id + '/');
    },
    getAll(search = '') {
        return Axios.get(`/app-settings/outbound-processing-methods/?search=${search}`);
    },
    getByDestinationAndEuralCode(waste_stream = '') {
        return Axios.get(
            `/app-settings/outbound-processing-methods/?destination=${waste_stream.destination.id}&eural_code=${waste_stream.eural_code.id}`
        );
    },
};

const Institute = {
    create(institute) {
        return Axios.post('/app-settings/institutes/save/', institute, header);
    },
    update(institute) {
        return Axios.put('/app-settings/institutes/save/' + institute.id + '/', institute, header);
    },
    delete(institute_id) {
        return Axios.delete('/app-settings/institutes/delete/' + institute_id + '/', header);
    },
    get(institute_id) {
        return Axios.get('/app-settings/institutes/' + institute_id + '/', header);
    },
    getEducationalOptions() {
        return Axios.get('/hrm/educational-institutes/options/');
    },
    getCertifyingOptions() {
        return Axios.get('/hrm/certifying-institutes/options/');
    },
};

const Industry = {
    create(industry) {
        return Axios.post('/industries/save/', industry, header);
    },
    update(industry) {
        return Axios.put('/industries/save/' + industry.id + '/', industry, header);
    },
    delete(id) {
        return Axios.delete('/industries/delete/' + id + '/', header);
    },
    searchIndustries(search) {
        return Axios.get(`/industries/options/?q=${search}`);
    },
    getIndustry(id) {
        return Axios.get('/industries/' + id + '/');
    },
    getIndustries() {
        return Axios.get('/industries/list/');
    },
};

const Employee = {
    searchEmployees(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/hrm/employees/options/?${get_params}`, header);
    },
    list() {
        return Axios.get(`/hrm/employees/`, header);
    },
    get(id) {
        return Axios.get(`/hrm/employees/${id}/`, header);
    },
    facts(query_data) {
        return Axios.get(`/hrm/facts/?${query_data}`, header);
    },
    create(instance) {
        return Axios.post('/hrm/employees/save/', instance, header);
    },
    update(instance) {
        return Axios.put('/hrm/employees/save/' + instance.id + '/', instance, header);
    },
    getUser(user_id) {
        return Axios.get('/hrm/employees/user/' + user_id + '/');
    },
    delete(contact_id) {
        return Axios.delete('/hrm/employees/delete/' + contact_id + '/');
    },
    runExpirationChecks() {
        return Axios.post('/hrm/employees/run_expiration_tasks/');
    },
};

const TimeRegistration = {
    all() {
        return Axios.get(`/hrm/time-registration/`);
    },
    filter(params) {
        return Axios.get(`/hrm/time-registration/${params.url}`, params);
    },
    approve(instance) {
        return Axios.post('/hrm/time-registration/approve/' + instance.id + '/', instance);
    },
    disapprove(instance) {
        return Axios.post('/hrm/time-registration/disapprove/' + instance.id + '/', instance);
    },
    approve_changes(instance) {
        return Axios.post('/hrm/time-registration/approve_changes/' + instance.id + '/', instance);
    },
    get(id) {
        return Axios.get(`/hrm/time-registration/${id}/`);
    },
    create(instance) {
        return Axios.post('/hrm/time-registration/save/', instance, header);
    },
    item_add(instance, data) {
        return Axios.post(`/hrm/time-registration/${instance.id}/item-add/`, data, header);
    },
    update(instance) {
        return Axios.put('/hrm/time-registration/save/' + instance.id + '/', instance, header);
    },
    delete(contact_id) {
        return Axios.delete('/hrm/time-registration/delete/' + contact_id + '/');
    },
    getEmployees() {
        return Axios.get('/hrm/time-registration/employees/');
    },
    getItemTypes() {
        return Axios.get('/hrm/time-registration/item-types/');
    },
};

const Task = {
    getDataTableForUser(user_id) {
        return Axios.get(`/tasks/datatable/${user_id}/`, header);
    },
    getDataTableForAll() {
        return Axios.get(`/tasks/datatable/`, header);
    },
    getKanban() {
        return Axios.get(`/tasks/kanban/`, header);
    },
    getKanbanStructure() {
        return Axios.get(`/tasks/kanban/structure/`, header);
    },
    setKanbanStructure(structure) {
        return Axios.put('/tasks/kanban/structure/', structure, header);
    },
    getDataTableForAllClosed() {
        return Axios.get(`/tasks/datatable/closed/`, header);
    },
    searchRelatedObjectsForModel(model, search) {
        return Axios.get('/tasks/search/' + model + '/?q=' + search, header);
    },
    create(task) {
        return Axios.post('/tasks/create/', task, header);
    },
    update(task) {
        return Axios.put('/tasks/update/' + task.id + '/', task, header);
    },
    get(task_id) {
        return Axios.get('/tasks/' + task_id + '/', header);
    },
    delete(task_id) {
        return Axios.delete('/tasks/delete/' + task_id + '/', header);
    },
    bulkDelete(task_ids) {
        return Axios.post('/tasks/bulk-delete/', { task_ids }, header);
    },
    bulkClose(task_ids) {
        return Axios.post('/tasks/bulk-close/', { task_ids }, header);
    },
    modelOptions() {
        return Axios.get('/tasks/model-options/', header);
    },
    fetchStatuses() {
        return Axios.get('/tasks/status-options/', header);
    },
    fetchPriorities() {
        return Axios.get('/tasks/priorities-options/', header);
    },
};
const EuralCodes = {
    get(id) {
        return Axios.get(`/eural-codes/${id}/`);
    },
    list() {
        return Axios.get(`/eural-codes/list/`);
    },
    getEuralCodes() {
        return Axios.get('/eural-codes/list/');
    },
    getActiveEuralCodes() {
        return Axios.get('/eural-codes/active/list/');
    },
    searchEuralCodes(search, simple = false, args = '') {
        return Axios.get(`/eural-codes/search/?q=${search}&simple=${simple}&${args}`, header);
    },

    getEuralCode(id, ...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/eural-codes/${id}/?${get_params}`);
    },
    createEuralCode(eural_code) {
        return Axios.post(`/eural-codes/save/`, eural_code, header);
    },
    updateEuralCode(eural_code) {
        return Axios.put(`/eural-codes/save/${eural_code.id}/`, eural_code, header);
    },
    deleteEuralCode(id) {
        return Axios.delete(`/eural-codes/delete/${id}/`, header);
    },
    getTunnelCodes() {
        return Axios.get(`/eural-codes/get-tunnel-codes/`, header);
    },
    getADRClasses() {
        return Axios.get(`/eural-codes/get-adr-classes/`, header);
    },
    getPackagingGroup() {
        return Axios.get(`/eural-codes/get-packaging-group/`, header);
    },
};

const TransportUnit = {
    runExpirationChecks() {
        return Axios.post('/transports/transport-units/run_expiration_tasks/');
    },
};

const PhysicalProperties = {
    savePhysicalProperty(data) {
        return Axios.post(`/physical-properties/save/`, data, header);
    },
    deletePhysicalProperty(id) {
        return Axios.delete(`/physical-properties/delete/${id}/`, header);
    },
    updatePhysicalProperty(data) {
        return Axios.put(`/physical-properties/save/${data.id}/`, data, header);
    },
    getPhysicalProperties() {
        return Axios.get(`/physical-properties/list/`, header);
    },
};

const PackagingTypes = {
    savePackagingType(data) {
        return Axios.post(`/packaging-types/save/`, data, header);
    },
    deletePackagingType(id) {
        return Axios.delete(`/packaging-types/delete/${id}/`, header);
    },
    updatePackagingType(data) {
        return Axios.put(`/packaging-types/save/${data.id}/`, data, header);
    },
    getPackagingTypes() {
        return Axios.get(`/packaging-types/list/`, header);
    },
};

const Storage = {
    getAllStorageUnitsForEstablishment(establishment_id = false) {
        return Axios.get(`/storage/per-establishment/${establishment_id}/`);
    },
    getAllowedStorageUnitsForCollectionWasteGroup(collection_waste_group_id, establishment_id) {
        return Axios.get(`/storage/per-collection-waste-group/${collection_waste_group_id}/${establishment_id}/`);
    },
    getAllowedStorageUnitsForEuralCode(establishment_id, eural_code_id, amount = 0) {
        if (amount === null || amount === undefined) {
            amount = 0;
        }
        return Axios.get(`/storage/per-eural-code/${eural_code_id}/${establishment_id}/${amount}/`);
    },
    get(id) {
        return Axios.get(`/hrm/employees/${id}/`);
    },
    searchTypes(search) {
        return Axios.get(`/storage/types/?q=${search}`);
    },
    searchWasteGroups(search = '') {
        return Axios.get(`/storage/storage-waste-groups/search/?q=${search}`);
    },
    searchEuralCodes(
        search = '',
        selected = '',
        use = '',
        licensed_for_collection = 0,
        establishment_id = '',
        simple = false
    ) {
        return Axios.get(
            `/eural-codes-by-type/search/collection/?q=${search}&first_selected=${selected}&use=${use}&licensed_for_collection=${licensed_for_collection}&establishment_id=${establishment_id}&simple=${simple}`
        );
    },
    searchEuralCodesWithWasteGroup(
        collection_waste_group_id,
        search = '',
        selected = '',
        use = '',
        licensed_for_collection = 0,
        establishment_id = ''
    ) {
        return Axios.get(
            `/eural-codes-by-waste-group/${collection_waste_group_id}/search/?q=${search}&first_selected=${selected}&use=${use}&licensed_for_collection=${licensed_for_collection}&establishment_id=${establishment_id}`
        );
    },
    searchStorage(search, tanks = '', establishment = '') {
        return Axios.get(`/storage/search/?q=${search}&tanks=${tanks}&establishment=${establishment}`);
    },
    getByRelationAndEuralCode(relation_id, eural_code_id = '') {
        return Axios.get(`/storage/by-relation-eural-code/?relation_id=${relation_id}&eural_code_id=${eural_code_id}`);
    },
    searchStorageWithEstablishments(search, tanks = '', order_by = 'reference') {
        return Axios.get(`/storage/search/?q=${search}&establishments=true&tanks=${tanks}&order_by=${order_by}`);
    },
    searchStorageToProcess(bulkType, bulkId, all, date) {
        let params = all ? '?all=true' : '';
        if (date) {
            params += (params ? '&' : '?') + `processed_at=${date}`;
        }
        return Axios.get(`/storage/by-bulk/${bulkType}/${bulkId}/${params}`);
    },
    getStorage(id) {
        return Axios.get(`/storage/${id}/`);
    },
    updateStorage(data) {
        return Axios.put(`/storage/save/${data.id}/`, data, header);
    },
    createStorageUnit(data) {
        return Axios.post(`/storage/save/`, data, header);
    },
    deleteStorage(id) {
        return Axios.delete('/storage/delete/' + id + '/');
    },
    deactivateStorage(id) {
        return Axios.post('/storage/deactivate/' + id + '/');
    },
    activateStorage(id) {
        return Axios.post('/storage/activate/' + id + '/');
    },
    createWasteGroup(data) {
        return Axios.post('/storage/storage-waste-groups/save/', data, header);
    },
    updateWasteGroup(data) {
        return Axios.put('/storage/storage-waste-groups/save/' + data.id + '/', data, header);
    },
    deleteWasteGroup(id) {
        return Axios.delete('/storage/storage-waste-groups/delete/' + id + '/', header);
    },
    getWasteGroup(id) {
        return Axios.get('/storage/storage-waste-groups/' + id + '/', header);
    },
    checkSortingNumberExistance(number = null, id = 'null') {
        return Axios.get(`/storage/number-exists/${number}/${id}/`);
    },
    getPDFWithQRCode(storage_unit_id, relation_id, size = 'medium', lang = '') {
        const langParam = lang ? `?lang=${encodeURIComponent(lang)}` : '';
        return Axios.get(`/storage/get-qr-pdf/${storage_unit_id}/${relation_id}/${size}/${langParam}`, {
            responseType: 'blob',
        });
    },
    getNewOrderInfoForStorageUnit(payload, lang = '') {
        const langParam = lang ? `?lang=${encodeURIComponent(lang)}` : '';
        return Axios.get(`/create-from-qr/${payload}/${langParam}`);
    },
    createNewOrderInfoForStorageUnit(payload, data, lang = '') {
        const langParam = lang ? `?lang=${encodeURIComponent(lang)}` : '';
        return Axios.post(`/create-from-qr/${payload}/${langParam}`, data);
    },
    setConnection(from, to) {
        return Axios.post(`/storage/storage-waste-groups/connection/${from}/${to}/`);
    },
    existingConnections() {
        return Axios.get(`/storage/storage-waste-groups/connections/`);
    },
    connectionsPerStorageUnit(date) {
        return Axios.get(`/storage/storage-units/with-connections/${date}/`);
    },
};

const StorageHistory = {
    getList(storage_unit_id) {
        return Axios.get(`/storage-history-list/${storage_unit_id}/`);
    },
    get(id) {
        return Axios.get(`/storage-history/${id}/`);
    },
    update(id, data) {
        return Axios.put(`/storage-history/${id}/`, data);
    },
    delete(id) {
        return Axios.delete(`/storage-history/${id}/`);
    },
    create(id, data) {
        return Axios.post(`/storage-history/`, data);
    },
    checkUpdate(id, data) {
        return Axios.put(`/storage-history-check/${id}/`, data);
    },
    checkDelete(id) {
        return Axios.delete(`/storage-history-check/${id}/`);
    },
    checkCreate(data) {
        return Axios.post(`/storage-history-check/`, data);
    },
};

const Collection = {
    get(id) {
        return Axios.get(`/hrm/employees/${id}/`);
    },
    setConnection(euralData) {
        return Axios.post('/eural-codes-by-type/collection/', { eural_data: euralData }, header);
    },
    searchTypes(search) {
        return Axios.get(`/collection/types/?q=${search}`);
    },
    searchEuralCodes(search = '', selected) {
        return Axios.get(`/eural-codes-by-type/search/collection/?q=${search}&first_selected=${selected}`);
    },
    searchCollection(search) {
        return Axios.get(`/collection/collection/search/?q=${search}`);
    },
    getCollection(id) {
        return Axios.get(`/collection/collection/${id}/`);
    },
    updateCollection(data) {
        return Axios.put(`/collection/collection/save/${data.id}/`, data, header);
    },
    createCollection(data) {
        return Axios.post(`/collection/collection/save/`, data, header);
    },
    deleteCollection(id) {
        return Axios.delete('/collection/collection/delete/' + id + '/');
    },
    createWasteGroup(data) {
        return Axios.post('/collection/collection-waste-groups/save/', data, header);
    },
    updateWasteGroup(data) {
        return Axios.put('/collection/collection-waste-groups/save/' + data.id + '/', data, header);
    },
    deleteWasteGroup(id) {
        return Axios.delete('/collection/collection-waste-groups/delete/' + id + '/', header);
    },
    activateWasteGroup(id) {
        return Axios.post('/collection/collection-waste-groups/activate/' + id + '/', header);
    },
    getWasteGroup(id) {
        return Axios.get('/collection/collection-waste-groups/' + id + '/', header);
    },
    getWasteGroupForEdit(id) {
        return Axios.get(`/collection/collection-waste-groups/${id}/`, header);
    },
    getNewWasteStreamNumber(establishment_id) {
        return Axios.get('/waste-streams/new-waste-stream-number/' + establishment_id + '/', header);
    },
    getDriverForOriginAndEuralCode(origin_id, eural_code_id) {
        return Axios.get(`/collection/get-driver-for-origin/${origin_id}/${eural_code_id}/`);
    },
};

const Transport = {
    get(id) {
        return Axios.get(`/transports/${id}/`, header);
    },
    list() {
        return Axios.get(`/transports/`, header);
    },
    getByLicensePlate(license_plate) {
        return Axios.get(`/transports/license_plate/${license_plate}/`, header);
    },
    getTypes() {
        return Axios.get('/transports/types/', header);
    },
    checkLicensePlate(license_plate) {
        return Axios.get('/transports/get-license-plate-data/' + license_plate + '/');
    },
    create(unit) {
        return Axios.post('/transports/save/', unit, header);
    },
    update(unit) {
        return Axios.put('/transports/save/' + unit.id + '/', unit, header);
    },
    delete(transport_unit) {
        return Axios.delete(`/transports/delete/${transport_unit.id}/`);
    },
    fleetGoCompare(data) {
        return Axios.post(`/transports/compare/fleetgo/`, data, header);
    },
    getCollectionBulksWithWeighings(establishment_id) {
        return Axios.get(`/transports/collection-bulks-with-weighings/${establishment_id}/`);
    },
    getDeliveryBulksWithWeighings(establishment_id) {
        return Axios.get(`/delivery-orders/delivery-bulks-with-weighings/${establishment_id}/`);
    },
    getNotProcessedOutboundBulks() {
        return Axios.get(`/bulks/list/outbound/?filters[table]=not_processed`);
    },
    fetchTransportUnitOptions() {
        return Axios.get('/transport/transport-units/');
    },
};

const TransportNumber = {
    get(id) {
        return Axios.get(`/transports-numbers/${id}/`, header);
    },
    create(number) {
        return Axios.post('/transports-numbers/save/', number, header);
    },
    update(number) {
        return Axios.put(`/transports-numbers/save/${number.id}/`, number, header);
    },
    delete(id) {
        return Axios.delete(`/transports-numbers/delete/${id}/`, header);
    },
};

const Notification = {
    getNotificationsByUserId(page, readStatus) {
        return Axios.get(
            `/notifications/get-states/`,
            {
                params: {
                    read_status: readStatus,
                    page: page,
                },
            },
            header
        );
    },
    getNotificationById(notification_id) {
        return Axios.get(`/notifications/get/${notification_id}/`, header);
    },
    getState(id) {
        return Axios.get(`/notifications/get/${id}/`, header);
    },
    switchReadStatusNotification(pk) {
        return Axios.put(`/notifications/read/${pk}/`, header);
    },
    readNotification(pk) {
        return Axios.put(`/notifications/read/${pk}/force/`, header);
    },
    getNewNotificationsCount() {
        return Axios.get('/notifications/new/count/', header);
    },
    deleteState(pk) {
        return Axios.delete(`/notifications/delete-state/${pk}/`, header);
    },
    readAll() {
        return Axios.post(`/notifications/read-all/`);
    },
    head() {
        return Axios.head('/notifications/head-states/');
    },
};

const Planning = {
    getDrivers(params) {
        return Axios.get('/planning/drivers/', params);
    },
    getDriverById(driver_id, params) {
        return Axios.get(`/planning/drivers/${driver_id}/`, params);
    },
    getSimpleDrivers(params) {
        return Axios.get('/planning/simple-drivers/', params);
    },
    getActivities(params) {
        return Axios.get('/planning/activities/', params);
    },
    getActivitiesForDriver(driver_id, params) {
        return Axios.get(`/planning/driver/${driver_id}/activities/`, params);
    },
    getMarkers(params) {
        return Axios.get('/planning/markers/', params);
    },
    getSimpleTransportUnits(params) {
        return Axios.get('/planning/transport-units/', params);
    },
    createActivity(data, url = '') {
        return Axios.post(`/planning/activities/${url}`, data);
    },
    updateActivity(event_id, params) {
        return Axios.put(`/planning/activities/${event_id}/`, params);
    },
    deleteActivity(event_id, url = '') {
        return Axios.delete(`/planning/activities/${event_id}/delete/${url}`);
    },
    getAccessToken(params) {
        return Axios.get('/planning/access-token/', params);
    },
    getStream(token) {
        return Axios.get(
            'https://api.fleetgo.com/api/Equipment/Subscribe?groupId=0&vehicleCategoryId=0&driverCategoryId=0',
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
    },
    getFleetGoEquipment(token) {
        return Axios.get('https://api.fleetgo.com/api/Equipment/GetFleet?groupId=0&hasDeviceOnly=true', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },
    getDriverHome(params) {
        return Axios.get('/planning/home-locations/', params);
    },
    getDriverRoadMap(driver_id, params) {
        return Axios.get(`/planning/road-map/${driver_id}/`, params);
    },
    getDriverRouteSource(driver_id, params) {
        return Axios.get(`/planning/route-source/${driver_id}/`, params);
    },
    getDriversRouteSource(params) {
        return Axios.get(`/planning/route-source/`, params);
    },
    createDriverRouteSource(params) {
        return Axios.post(`/planning/route-source/`, params);
    },
    updateDriverRouteSource(source_id, params) {
        return Axios.put(`/planning/route-source/${source_id}/update/`, params);
    },
    getDaysCalendarSources() {
        return Axios.get('/planning/days-calendar/sources/');
    },
    getDaysCalendarOutOfOfficeUsers(search) {
        return Axios.get('/planning/days-calendar/out-of-office-users/', {
            params: { q: search || '' },
        });
    },
    updateDaysCalendarPreferences(enabled_sources) {
        return Axios.patch('/planning/days-calendar/preferences/', { enabled_sources });
    },
    getDaysCalendarEvents(params) {
        return Axios.get('/planning/days-calendar/events/', { params });
    },
    getDaysCalendarCustomCalendars() {
        return Axios.get('/planning/days-calendar/custom-calendars/');
    },
    createDaysCalendarCustomCalendar(data) {
        return Axios.post('/planning/days-calendar/custom-calendars/', data);
    },
    updateDaysCalendarCustomCalendar(id, data) {
        return Axios.put(`/planning/days-calendar/custom-calendars/${id}/`, data);
    },
    deleteDaysCalendarCustomCalendar(id) {
        return Axios.delete(`/planning/days-calendar/custom-calendars/${id}/`);
    },
    createDaysCalendarEvent(data) {
        return Axios.post('/planning/days-calendar/user-events/', data);
    },
    updateDaysCalendarEvent(id, data) {
        return Axios.put(`/planning/days-calendar/user-events/${id}/`, data);
    },
    deleteDaysCalendarEvent(id) {
        return Axios.delete(`/planning/days-calendar/user-events/${id}/`);
    },
    createDaysCalendarOutOfOfficeEvent(data) {
        return Axios.post('/planning/days-calendar/out-of-office-events/', data);
    },
    updateDaysCalendarOutOfOfficeEvent(id, data) {
        return Axios.put(`/planning/days-calendar/out-of-office-events/${id}/`, data);
    },
    deleteDaysCalendarOutOfOfficeEvent(id) {
        return Axios.delete(`/planning/days-calendar/out-of-office-events/${id}/`);
    },
    getDaysCalendarEventTypes() {
        return Axios.get('/planning/days-calendar/event-types/');
    },
    getDaysCalendarEventType(id) {
        return Axios.get(`/planning/days-calendar/event-types/${id}/`);
    },
    createDaysCalendarEventType(data) {
        return Axios.post('/planning/days-calendar/event-types/', data);
    },
    updateDaysCalendarEventType(id, data) {
        return Axios.put(`/planning/days-calendar/event-types/${id}/`, data);
    },
    deleteDaysCalendarEventType(id) {
        return Axios.delete(`/planning/days-calendar/event-types/${id}/`);
    },
};

const WasteStream = {
    create(data) {
        return Axios.post('/waste-streams/save/', data, header);
    },
    get(id) {
        return Axios.get(`/waste-streams/${id}/`, header);
    },
    history(id, limit, type = '', statuses = []) {
        const statusesParam = statuses.length ? `&statuses=${statuses.join(',')}` : '';
        return Axios.get(`/waste-streams/${id}/history/?limit=${limit}&type=${type}${statusesParam}`, header);
    },
    latestWeight(id, type = '') {
        return Axios.get(`/waste-streams/${id}/history/latest-weight/?type=${type}`, header);
    },
    latestLoadingTime(id, type = '') {
        return Axios.get(`/waste-streams/${id}/history/latest-loading-time/?type=${type}`, header);
    },
    changeRiskState(id) {
        return Axios.put(`/waste-streams/${id}/change-risk-status/`);
    },
    invalidateWasteStream(id) {
        return Axios.put(`/waste-streams/${id}/invalidate-waste-stream/`);
    },
    changeRiskDescription(data) {
        return Axios.put(`/waste-streams/${data.id}/change-risk-description/`, data);
    },
    updateTags(data) {
        return Axios.put(`/waste-streams/${data.id}/update-tags/`, data);
    },
    deactivateInsight(data) {
        return Axios.post(`/waste-streams/deactivate-insight/${data.id}/`, {
            reason: data.reason,
        });
    },
    activateInsight(data) {
        return Axios.post(`/waste-streams/activate-insight/${data.id}/`);
    },
};

const Driver = {
    getEmployee(driverId) {
        return Axios.get(`/drivers/${driverId}`, header);
    },
    all() {
        return Axios.get(`/drivers/`, header);
    },
    search(search) {
        return Axios.get(`/drivers/search/?q=${search}`, header);
    },
    saveRegionsForDrivers(data) {
        return Axios.post('/app-settings/driver-regions/save/', data, header);
    },
    createDiversion(data) {
        return Axios.post('/app-settings/driver-diversions/create/', data, header);
    },
    getDiversionsForDriver(driverId, status) {
        return Axios.get(`/app-settings/driver-diversions/datatable/?driver_id=${driverId}&status=${status}`, header);
    },
    getDiversion(diversionId) {
        return Axios.get(`/app-settings/driver-diversions/${diversionId}/`, header);
    },
    updateDiversion(diversionId, data) {
        return Axios.put(`/app-settings/driver-diversions/${diversionId}/`, data, header);
    },
    runDiversionTasks() {
        return Axios.post('/app-settings/driver-diversions/run-tasks/', {}, header);
    },
    deleteDiversion(diversionId) {
        return Axios.delete(`/app-settings/driver-diversions/${diversionId}/`, header);
    },
    getRegionsForDriver(driver_id) {
        return Axios.get(`/app-settings/driver-regions/${driver_id}/`, header);
    },
    getDriversAndRegions() {
        return Axios.get(`/app-settings/driver-regions/all/`, header);
    },
};

const Region = {
    all() {
        return Axios.get(`/regions/`, header);
    },
    search(search) {
        return Axios.get(`/regions/search/?q=${search}`, header);
    },
    getForDriver(driverId) {
        return Axios.get(`/app-settings/driver-regions/${driverId}/`, header);
    },
};

const CollectionWasteGroup = {
    all() {
        return Axios.get(`/collection-waste-groups/`, header);
    },
    search(search, fields = '') {
        return Axios.get(`/collection/collection-waste-groups/search/?q=${search}&${fields}`, header);
    },
    options() {
        return Axios.get(`/collection-waste-groups/options/`, header);
    },
};

const StorageWasteGroup = {
    all() {
        return Axios.get(`/storage/storage-waste-groups-list/`, header);
    },
    options() {
        return Axios.get(`/storage/storage-waste-groups/options/`, header);
    },
};

const Categories = {
    getAll() {
        return Axios.get(`/categories/`, header);
    },
    getCategory(category_id) {
        return Axios.get(`/categories/${category_id}/`, header);
    },
    saveCategory(category) {
        return Axios.post('/categories/', category, header);
    },
    updateCategory(category) {
        return Axios.put(`/categories/${category.id}/`, category, header);
    },
    deleteCategory(category_id) {
        return Axios.delete(`/categories/${category_id}/delete/`, header);
    },
};

const Language = {
    setLanguage(language) {
        const params = new FormData();
        params.append('language', language);
        return Axios.post(`/i18n/setlang/`, params, header);
    },
};

const Orders = {
    getTypes(short = 0) {
        return Axios.get(`/orders/types/?short=${short}`);
    },
    getStatuses() {
        return Axios.get('/orders/statuses/');
    },
    previewConcept(orderType, docType, orderData) {
        return Axios.post(`/api/orders/preview-concept/${orderType}/?doc_type=${docType}`, orderData, {
            responseType: 'blob',
        });
    },
    checkOrderNumber(orderType = 'collection', orderNumber = null, checkedOrder = null) {
        return Axios.get(`/${orderType}-orders/check-order-number/${orderNumber}/${checkedOrder}/`);
    },
    recurringOrderTemplatesByOrder(orderId, orderType) {
        return Axios.get(`/recurring-order-templates-by-order/${orderId}/?type=${orderType}`);
    },
    checkExceededInvoices(order_type, debtor_id) {
        return Axios.get(`/orders/check-exceeded-invoices/${order_type}/${debtor_id}/`);
    },
};

const CollectionOrder = {
    create(order) {
        return Axios.post('/collection-orders/', order);
    },
    get(order_id, is_billing = false, fields = '') {
        return Axios.get(`/collection-orders/${order_id}/?${fields}`);
    },
    getForBilling(order_id, fields = '') {
        return Axios.get(`/collection-orders/${order_id}/?${fields}`);
    },
    update(order) {
        return Axios.put(`/collection-orders/${order.id}/`, order);
    },
    partialUpdate(order) {
        return Axios.patch(`/collection-orders/${order.id}/`, order);
    },
    partialUpdatePlannedDate(order) {
        return Axios.patch(`/collection-orders/${order.id}/planned-date/`, order);
    },
    delete(order, reason) {
        return Axios.delete(`/collection-orders/${order.id}/`, {
            data: {
                reason: reason,
            },
        });
    },
    deleteAnon(order, reason) {
        return Axios.delete(`/collection-order-delete/${order.id}/`, {
            data: {
                reason: reason,
            },
        });
    },
    forMap(params) {
        return Axios.get(`/collection-orders/for-map/`, params);
    },
    isCollectionOrderRecurring(data) {
        return Axios.post('/collection-orders/is-recurring/', data);
    },
    getByDisposerId(disposer_id) {
        return Axios.get('/collection-orders/by-disposer/' + disposer_id + '/');
    },
    driverChange(data) {
        return Axios.post('/collection-orders/driver-change/', data);
    },
    debtorChange(orderId, data) {
        return Axios.post(`/collection-orders/${orderId}/debtor-change/`, data);
    },
    fetchSenderRoles() {
        return Axios.get('/collection-orders/sender-roles/');
    },
    fetchTransporterTypes() {
        return Axios.get('/collection-orders/transporter-types/');
    },
    fetchMediumChoices() {
        return Axios.get('/collection-orders/medium-choices/');
    },
    generateOrdersList(driver = '', filter = '?') {
        const query = filter || '?';
        if (!driver) {
            return Axios.get(`/collection-orders/print-orders-per-driver/${query}`);
        }
        const separator = query !== '?' ? '&' : '';
        return Axios.get(`/collection-orders/print-orders-per-driver/${query}${separator}force_driver=${driver}`);
    },
    sendOrdersListToDrivers(data) {
        return Axios.post('/collection-orders/print-orders-per-driver/send/', data);
    },
    getDefaultTransportRole() {
        return Axios.get(`/collection-orders/default-transport-role/`);
    },
    check(order) {
        return Axios.post(`/collection-orders/check/`, order);
    },
    getTags() {
        return Axios.get('/collection-orders/tags/', header);
    },
    getPredictions(order_type, params) {
        return Axios.get(`/forecast/${order_type}/chart/?${params}`);
    },
    getSignedDocuments(hash) {
        return Axios.get(`/api/get-signed-documents/${hash}/`);
    },
    sendMails(hash, data) {
        return Axios.post(`/api/send-tl-by-mail/${hash}/`, data);
    },
    sendMailsWithLogs(order_type, order_id, data) {
        return Axios.post(`/api/send-order-tl-on-mail-with-logs/order/${order_type}/${order_id}/`, data);
    },
    regenerateTransportLetter(order_id) {
        return Axios.get(`/collection-orders/${order_id}/regenerate-transport-letter/`);
    },
    resendTransportLetter(data, order_id) {
        return Axios.post(`/collection-orders/${order_id}/transport-letter/send/`, data);
    },
    resendTransportLetterTask(task_id) {
        return Axios.post(`/collection-orders/send-task/${task_id}/resend/`);
    },
    getSentLog(order_id) {
        return Axios.get(`/collection-orders/${order_id}/transport-letter/sent-log/`);
    },
    getAnalyseForOrder(order_id) {
        return Axios.get(`/collection-orders/${order_id}/analyse/`);
    },
};

const CollectionOrderTemplate = {
    get(id) {
        return Axios.get(`/collection-order-template/${id}/`);
    },
    saveRecurrence(data) {
        return Axios.put(`/collection-order-template/${data.order_template_id}/recurrence/save/`, data);
    },
    deactivate(id) {
        return Axios.put(`/collection-order-template/deactivate/${id}/`);
    },
};

const OutboundOrder = {
    create(order, isBlob = false) {
        if (isBlob) {
            return Axios.post('/outbound-orders/', order, { responseType: 'blob' });
        } else {
            return Axios.post('/outbound-orders/', order);
        }
    },
    update(order, isBlob = false) {
        if (isBlob) {
            return Axios.put(`/outbound-orders/${order.id}/`, order, { responseType: 'blob' });
        } else {
            return Axios.put(`/outbound-orders/${order.id}/`, order);
        }
    },
    partialUpdate(order) {
        return Axios.patch(`/outbound-orders/${order.id}/`, order);
    },
    partialUpdatePlannedDate(order) {
        return Axios.patch(`/outbound-orders/${order.id}/planned-date/`, order);
    },
    get(order_id, fields = '') {
        return Axios.get(`/outbound-orders/${order_id}/?${fields}`);
    },
    getToManage(order_id, fields = '') {
        return Axios.get(`/outbound-orders/${order_id}/manage/?${fields}`);
    },
    delete(order, force = false, reason) {
        return Axios.delete(`/outbound-orders/${order.id}/?force=${force}`, {
            data: {
                reason: reason,
            },
        });
    },
    manage: function (data) {
        return Axios.put(`/outbound-orders/${data.id}/manage/`, data);
    },
    process: function (data) {
        return Axios.put(`/outbound-orders/${data.id}/process/`, data);
    },
    debtorChange(orderId, data) {
        return Axios.post(`/outbound-orders/${orderId}/debtor-change/`, data);
    },
};

const OutboundOrderTemplate = {
    get(id, fields) {
        if (fields) {
            return Axios.get(`/outbound-order-template/${id}/?fields=${fields}`);
        } else {
            return Axios.get(`/outbound-order-template/${id}/`);
        }
    },
    deactivate(id) {
        return Axios.post(`/outbound-order-template/${id}/deactivate/`);
    },
    partialUpdate(order_template) {
        return Axios.patch(`/outbound-order-template/${order_template.id}/`, order_template);
    },
    getOpenOrders(id) {
        return Axios.get(`/outbound-order-template/${id}/open-orders/`);
    },
};

const DeliveryOrder = {
    create(order, isBlob = false) {
        if (isBlob) {
            return Axios.post('/delivery-orders/', order, { responseType: 'blob' });
        } else {
            return Axios.post('/delivery-orders/', order);
        }
    },
    update(order, isBlob = false) {
        if (isBlob) {
            return Axios.put(`/delivery-orders/${order.id}/`, order, { responseType: 'blob' });
        } else {
            return Axios.put(`/delivery-orders/${order.id}/`, order);
        }
    },
    partialUpdate(order) {
        return Axios.patch(`/delivery-orders/${order.id}/`, order);
    },
    partialUpdatePlannedDate(order) {
        return Axios.patch(`/delivery-orders/${order.id}/planned-date/`, order);
    },
    get(order_id, fields = '') {
        return Axios.get(`/delivery-orders/${order_id}/?${fields}`);
    },
    delete(order, reason) {
        return Axios.delete(`/delivery-orders/${order.id}/`, {
            data: {
                reason: reason,
            },
        });
    },
    filterByParams(disposer_id, sender_id, debtor_id) {
        return Axios.get(
            `/delivery-order-template/datatable/?disposer_id=${disposer_id}&sender_id=${sender_id}&debtor_id=${debtor_id}`
        );
    },
    getAnalyseForOrder(order_id) {
        return Axios.get(`/delivery-orders/${order_id}/analyse/`);
    },
    debtorChange(orderId, data) {
        return Axios.post(`/delivery-orders/${orderId}/debtor-change/`, data);
    },
};

const PreflightQueue = {
    approve(modelLabel, orderPk, reason) {
        return Axios.post(`/preflight/orders/${modelLabel}/${orderPk}/approve/`, { reason });
    },
    reject(modelLabel, orderPk, reason) {
        return Axios.post(`/preflight/orders/${modelLabel}/${orderPk}/reject/`, { reason });
    },
    reset(modelLabel, orderPk) {
        return Axios.post(`/preflight/orders/${modelLabel}/${orderPk}/reset/`);
    },
    resubmit(modelLabel, orderPk) {
        return Axios.post(`/preflight/orders/${modelLabel}/${orderPk}/resubmit/`);
    },
};

const InternalOrder = {
    create(order, isBlob = false) {
        if (isBlob) {
            return Axios.post('/internal-orders/', order, { responseType: 'blob' });
        } else {
            return Axios.post('/internal-orders/', order);
        }
    },
    update(order, isBlob = false) {
        if (isBlob) {
            return Axios.put(`/internal-orders/${order.id}/`, order, { responseType: 'blob' });
        } else {
            return Axios.put(`/internal-orders/${order.id}/`, order);
        }
    },
    partialUpdate(order) {
        return Axios.patch(`/internal-orders/${order.id}/`, order);
    },
    partialUpdatePlannedDate(order) {
        return Axios.patch(`/internal-orders/${order.id}/planned-date/`, order);
    },
    get(order_id, fields = '') {
        return Axios.get(`/internal-orders/${order_id}/?${fields}`);
    },
    delete(order, reason) {
        return Axios.delete(`/internal-orders/${order.id}/`, {
            data: {
                reason: reason,
            },
        });
    },
    getFacts(filters) {
        return Axios.get(`/internal-orders/facts/${filters}`);
    },
    getSentLog(order_id) {
        return Axios.get(`/internal-orders/${order_id}/transport-letter/sent-log/`);
    },
};

const InternalOrderTemplate = {
    deactivate(id) {
        return Axios.put(`/internal-order-template/deactivate/${id}/`);
    },
};

const OutsourcedOrder = {
    create: function (data) {
        return Axios.post('/outsourced-orders/', data);
    },
    update: function (data) {
        return Axios.put(`/outsourced-orders/${data.id}/`, data);
    },
    partialUpdate(order) {
        return Axios.patch(`/outsourced-orders/${order.id}/`, order);
    },
    partialUpdatePlannedDate(order) {
        return Axios.patch(`/outsourced-orders/${order.id}/planned-date/`, order);
    },
    get: function (order_id, fields) {
        if (fields) {
            return Axios.get(`/outsourced-orders/${order_id}/?${fields}`);
        } else {
            return Axios.get(`/outsourced-orders/${order_id}/`);
        }
    },
    getToManage(order_id, fields = '') {
        return Axios.get(`/outsourced-orders/${order_id}/manage/?${fields}`);
    },
    delete(order, reason, notifyCollector = false) {
        return Axios.delete(`/outsourced-orders/${order.id}/`, {
            data: {
                reason: reason,
                notify_collector: notifyCollector,
            },
        });
    },
    manage: function (data) {
        return Axios.put(`/outsourced-orders/${data.id}/manage/`, data);
    },
    process: function (data) {
        return Axios.put(`/outsourced-orders/${data.id}/process/`, data);
    },
    accept: function (data) {
        return Axios.put(`/outsourced-orders/${data.id}/accept/`, data);
    },
    isOutsourcedOrderRecurring(data) {
        return Axios.post('/outsourced-orders/is-recurring/', data);
    },
    getByEuralCode(eural_code_id, order) {
        return Axios.get(
            `/outsourced-orders/${eural_code_id}/last/?disposer_id=${order.disposer.id}&sender_id=${order.sender.id}&origin_id=${order.origin.id}&collector_id=${order.collector.id}`
        );
    },
    getHistoricalOrder(template_id, ...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/outsourced-orders/historical/cargo/${template_id}/?${get_params}`);
    },
    debtorChange(orderId, data) {
        return Axios.post(`/outsourced-orders/${orderId}/debtor-change/`, data);
    },
    orderAcceptance(payload) {
        return Axios.get(`/order-acceptance/${payload}/`);
    },
    orderAccept(payload, planned_date = null) {
        return Axios.post(`/order-acceptance/${payload}/?planned_date=${planned_date}`);
    },
    orderToggleAccept(payload, reason = '') {
        return Axios.put(`/order-acceptance/${payload}/?declined_reason=${reason}`);
    },
    resetAcceptDeclinedStatus(payload) {
        return Axios.delete(`/order-acceptance/${payload}/`);
    },
};

const OutsourcedRegion = {
    get(id, fields = []) {
        return Axios.get(`/outsourced-orders/regions/${id}/?fields=${fields}`);
    },
    create(data) {
        return Axios.post(`/outsourced-orders/regions/`, data);
    },
    update(data) {
        return Axios.put(`/outsourced-orders/regions/${data.id}/`, data);
    },
    delete(id) {
        return Axios.delete(`/outsourced-orders/regions/${id}/`);
    },
    list(fields = []) {
        return Axios.get(`/outsourced-orders/regions/?fields=${fields}`);
    },
    getByEuralCodeAndOrigin(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/outsourced-orders/regions/?${get_params}`);
    },
};

const OutsourcedOrderTemplate = {
    get(id, fields) {
        if (fields) {
            return Axios.get(`/outsourced-order-template/${id}/?fields=${fields}`);
        } else {
            return Axios.get(`/outsourced-order-template/${id}/`);
        }
    },
    saveRecurrence(data) {
        return Axios.put(`/outsourced-order-template/${data.order_template_id}/recurrence/save/`, data);
    },
    getCargoByEuralCode(id) {
        return Axios.get(`/outsourced-order-template/cargo/eural-codes/${id}/`);
    },
};

const Catalog = {
    getItem(id, params_string = '') {
        return Axios.get(`/app-settings/catalog/items/${id}/?${params_string}`);
    },
    getPriceConfigurationsPerItem(item_id, params_string) {
        return Axios.get(`/app-settings/catalog/items/${item_id}/price-configurations/?${params_string}`);
    },
    getFilteredPriceConfigurationsPerItem(item_id, relation_id, preferred_date = 'null', fields = '') {
        return Axios.get(
            `/app-settings/catalog/extra-items-configurations-for-item/${item_id}/${relation_id}/${preferred_date}/?${fields}`
        );
    },
    createItem(item) {
        return Axios.post(`/app-settings/catalog/items/`, item);
    },
    updateItem(item) {
        return Axios.patch(`/app-settings/catalog/items/${item.id}/`, item);
    },
    checkSaveForWholeWasteGroup(item_id, configs_from_page) {
        return Axios.post(`/app-settings/catalog/items-check/${item_id}/`, configs_from_page);
    },
    deleteItem(id) {
        return Axios.delete(`/app-settings/catalog/items/${id}/`);
    },
    getItemsOptions(search, item_type = '') {
        return Axios.get(`/app-settings/catalog/items/options/?search=${search}&item_type=${item_type}`);
    },
    getItemsForType(type, ...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/app-settings/catalog/items/by-type/${type}/?${get_params}`, header);
    },
    getItemsOptionsByGroup(group_id) {
        return Axios.get(`/app-settings/catalog/items/options_by_group/${group_id}/`);
    },
    getItemGroup(id) {
        return Axios.get(`/app-settings/catalog/item-groups/${id}/`);
    },
    getItemsForGroup(group_id, ...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/app-settings/catalog/items/by-group/${group_id}/?${get_params}`, header);
    },
    getAllItemGroups(...kwargs) {
        let get_params = '';
        for (const obj of kwargs) {
            for (const [key, value] of Object.entries(obj)) {
                get_params += `${key}=${value}&`;
            }
        }
        return Axios.get(`/app-settings/catalog/item-groups/?${get_params}`);
    },
    updateItemGroup(item_group) {
        return Axios.put(`/app-settings/catalog/item-groups/${item_group.id}/`, item_group);
    },
    createItemGroup(item_group) {
        return Axios.post(`/app-settings/catalog/item-groups/`, item_group);
    },
    deleteItemGroup(id) {
        return Axios.delete(`/app-settings/catalog/item-groups/${id}/`);
    },
    searchItems(search) {
        return Axios.get(`/app-settings/catalog/items/search/?q=${search}`);
    },
    getPriceLists(search, billable = '', debtorable = '', isValid = '') {
        return Axios.get(
            `/app-settings/catalog/price-lists/search/?q=${search}&billable=${billable}&debtorable=${debtorable}&is_valid=${isValid}`
        );
    },
    createPriceList(price_list) {
        return Axios.post(`/app-settings/catalog/price-lists/`, price_list);
    },
    updatePriceList(price_list) {
        return Axios.patch(`/app-settings/catalog/price-lists/${price_list.id}/`, price_list);
    },
    deletePriceList(price_list_id) {
        return Axios.delete(`/app-settings/catalog/price-lists/${price_list_id}/`);
    },
    getPriceList(price_list_id, fields = '') {
        return Axios.get(`/app-settings/catalog/price-lists/${price_list_id}/?${fields}`);
    },
    getPriceListRelations(price_list_id) {
        return Axios.get(`/app-settings/catalog/price-lists-relations/${price_list_id}/`);
    },
    aggregateConfigurationsPerPriceList(id, date) {
        return Axios.get(`/app-settings/catalog/price-lists/${id}/aggregate-configurations/${date}/`);
    },
    getRevisionConfigurations(price_list_id, revision_id) {
        return Axios.get(`/app-settings/catalog/price-lists/${price_list_id}/revisions/${revision_id}/configurations/`);
    },
    getMainItemsForCollectionOrder(eural_code_id, relation_id, item_type_value, planned_date, fields = '') {
        return Axios.get(
            `/app-settings/catalog/main-item-configurations/${eural_code_id}/${relation_id}/${item_type_value}/${planned_date ? planned_date : null}/?${fields}`
        );
    },
    getMainItemsForCollectionOrderWithCollectionWasteGroup(
        eural_code_id,
        collection_waste_group_id,
        relation_id,
        item_type_value,
        planned_date,
        fields = ''
    ) {
        return Axios.get(
            `/app-settings/catalog/main-item-configurations/${eural_code_id}/${collection_waste_group_id}/${relation_id}/${item_type_value}/${planned_date ? planned_date : null}/?${fields}`
        );
    },
    getMainItemsForDeliveryOrder(eural_code_id, relation_id, item_type_value, expected_date) {
        return Axios.get(
            `/app-settings/catalog/main-item-configurations/${eural_code_id}/${relation_id}/${item_type_value}/${expected_date}/`
        );
    },
    getMainItemsForOutboundOrder(eural_code_id, relation_id, item_type_value, expected_date) {
        return Axios.get(
            `/app-settings/catalog/main-item-configurations/outbound/${eural_code_id}/${relation_id}/${item_type_value}/${expected_date}/`
        );
    },
    getNoEuralCodeItemsConfigurations(relation_id, item_type_value, planned_date, fields = null) {
        return Axios.get(
            `/app-settings/catalog/extra-items-configurations/${relation_id}/${item_type_value}/${planned_date}/?${fields}`
        );
    },
    getRelatedItemsConfigurationsForItem(relation_id, item_id, planned_date) {
        return Axios.get(
            `/app-settings/catalog/related-items-configurations/${relation_id}/${item_id}/${planned_date}/`
        );
    },
    fetchExactItemGroupsOptions() {
        return Axios.get(`/app-settings/catalog/exact-item-groups-options/`);
    },
    resetAllLocks() {
        return Axios.post('/api/reset-locks/');
    },
    updateItemPriceConfiguration(conf) {
        return Axios.patch(`/app-settings/catalog/item-price-configuration/${conf.id}/`, conf);
    },
};

const Holidays = {
    create(holiday) {
        return Axios.post(`/app-settings/public-holidays/create/`, holiday, header);
    },
    update(holiday) {
        return Axios.put(`/app-settings/public-holidays/update/${holiday.id}/`, holiday, header);
    },
    delete(holiday_id) {
        return Axios.delete(`/app-settings/public-holidays/delete/${holiday_id}/`, header);
    },
};

const Mail = {
    refresh(id) {
        return Axios.put(`/mail/sib/${id}/refresh/`, header);
    },
    fetchPaymentRequests(order_id, order_type, debtor_id) {
        return Axios.get(
            `/mail/payment-requests/?order_id=${order_id}&order_type=${order_type}&debtor_id=${debtor_id}`
        );
    },
};

const Sensor = {
    testConnection(serial_number) {
        return Axios.post(`/sensors/test-connection/`, { serial_number: serial_number }, { validateStatus: false });
    },
    loadSensors: () => Axios.get('/sensors/get-available/'),

    get(hwid) {
        return Axios.get(`/sensors/serial_number/${hwid}/`);
    },
    getById(id) {
        return Axios.get(`/sensors/${id}/`);
    },
    all() {
        return Axios.get(`/sensors/`);
    },
    getLogs(id, year) {
        return Axios.get(`/sensors/${id}/`);
    },
    getByOriginAndEuralCode(disposer, origin, eural_code) {
        return Axios.get(`/sensors/${disposer.id}/${origin.id}/${eural_code.id}/`);
    },
};

const Timeline = {
    // get(model, pk, radio_filter, q, ident, dateFrom, dateTo, dateLast) {
    //     let filters = `?model=${model}&pk=${pk}&radio_filter=${radio_filter}&q=${q ? q : ''}&ident=${ident}&dateFrom=${dateFrom}&dateTo=${dateTo}&dateLast=${dateLast}`
    //     return Axios.get(`/timeline/${filters}`, header)
    // },
    get(args) {
        return Axios.get(`/timeline/`, {
            headers: header,
            params: args,
        });
    },
};

const TimelineNote = {
    create(note) {
        let headers = {
            'Content-Type': 'multipart/form-data',
        };
        return Axios.post(`/timeline/notes/save/`, note, headers);
    },
    update(payload) {
        let headers = {
            'Content-Type': 'multipart/form-data',
        };
        return Axios.put(`/timeline/notes/save/${payload.id}/`, payload.formData, headers);
    },
    delete(note) {
        return Axios.delete(`/timeline/notes/delete/${note.id}/`, header);
    },
};

const Stock = {
    Mutations: {
        createMutation(data) {
            return Axios.post('/stock/mutations/create/', data);
        },
        getTypes(search) {
            return Axios.get('/stock/mutations/types/');
        },
        getMutation(id) {
            return Axios.get(`/stock/mutations/${id}/`);
        },
        saveMutationsManStock(data) {
            return Axios.post('/stock/mutations-manual-stock/save/', data);
        },
    },
    fetchMassBalance(start_date, end_date, type, establishment, pdf = false) {
        return Axios.get(
            `/stock/mass-balance/generate/?start_date=${start_date || ''}&end_date=${end_date || ''}&type=${type}&establishment=${establishment}&pdf=${pdf}`
        );
    },
    fetchStockInTime(date, establishment, time = null) {
        return Axios.get(
            `/stock/in-time/generate/?date=${date || ''}&establishment=${establishment}&time=${time || ''}`
        );
    },
    fetchEuralCodesForWasteGroup(start_date, end_date, type, establishment, waste_group_id) {
        return Axios.get(
            `/stock/mass-balance/eural-codes/?start_date=${start_date || ''}&end_date=${end_date || ''}&type=${type}&establishment=${establishment}&waste_group_id=${waste_group_id}`
        );
    },
    fetchInterProcessForWasteGroup(start_date, end_date, type, establishment, waste_group_id) {
        return Axios.get(
            `/stock/mass-balance/inter-process/?start_date=${start_date || ''}&end_date=${end_date || ''}&type=${type}&establishment=${establishment}&waste_group_id=${waste_group_id}`
        );
    },
    fetchCorrectStorageWasteGroup(storage_unit, date) {
        return Axios.get(`/stock/waste-group-by-date/${storage_unit}/${date}/`);
    },
    recalculateForUnits(data) {
        return Axios.post(`/stock/recalculate-for-storage-units/`, data);
    },
};

const Bulks = {
    getBulk(bulk_type, bulk_id, fields = '') {
        return Axios.get(`/bulk/${bulk_type}/${bulk_id}/?${fields}`, header);
    },
    partialUpdateBulk(bulk_type, bulk_id, data) {
        return Axios.patch(`/bulk/${bulk_type}/${bulk_id}/`, data, header);
    },
    printBulk(bulk_type, bulk_id) {
        return Axios.get(`/bulk/${bulk_type}/${bulk_id}/print-label/`, header);
    },
    deleteBulk(bulk_type, bulk_id) {
        return Axios.delete(`/bulk/${bulk_type}/${bulk_id}/`, header);
    },
    getBulksOptions(bulk_type, search, transport_unit_id = '', collection_waste_group = '') {
        if (search === undefined) {
            search = '';
        }
        return Axios.get(
            `/bulk/${bulk_type}/options/?search=${search}&transport_unit_id=${transport_unit_id}&collection_waste_group=${collection_waste_group}`,
            header
        );
    },
    getBulksOptionsByStatus(bulk_type, bulk_status) {
        return Axios.get(`/bulk/${bulk_type}/options/${bulk_status}/`, header);
    },
    processBulk(bulk_type, bulk_id, data) {
        return Axios.post(`/bulk/${bulk_type}/${bulk_id}/process/`, data, header);
    },
    createDeliveryBulk(data) {
        return Axios.post('/bulk/delivery/create/', data, header);
    },
    updateDeliveryBulk(data) {
        return Axios.put(`/bulk/delivery/${data.id}/update/`, data, header);
    },
    createInternalBulk(data) {
        return Axios.post('/bulk/internal/create/', data, header);
    },
    updateInternalBulk(data) {
        return Axios.put(`/bulk/internal/${data.id}/update/`, data, header);
    },
    createOutboundBulk(data) {
        return Axios.post('/bulk/outbound/create/', data, header);
    },
    updateOutboundBulk(data) {
        return Axios.put(`/bulk/outbound/${data.id}/update/`, data, header);
    },
    closeBulk(bulk_id, bulkType) {
        return Axios.put(`/api/close-bulks/dashboard/${bulk_id}/`, { bulk_type: bulkType }, header);
    },
    setDensity(bulk_id, density, bulkType) {
        return Axios.post(
            `/bulk/${bulkType}/${bulk_id}/set-density/`,
            {
                density: density,
            },
            header
        );
    },
    undoBulk(bulk_type, bulk_id) {
        return Axios.post(`/bulk/${bulk_type}/${bulk_id}/undo/`, {});
    },
    merge(bulk_type, source, destination) {
        return Axios.post(`/bulk/${bulk_type}/merge/${source}/${destination}/`);
    },
    reopenBulk(bulk_id, bulkType) {
        return Axios.put(`/bulk/${bulkType}/${bulk_id}/reopen/`, {});
    },
};

const AnalysisItems = {
    getUnitTypes() {
        return Axios.get('/analysis-items/unit-types/', header);
    },
    getOperators() {
        return Axios.get('/analysis-items/operators/', header);
    },
    getItems() {
        return Axios.get('/analysis-items/', header);
    },
    createItem(data) {
        return Axios.post('/analysis-items/', data, header);
    },
    updateItem(item_id, data) {
        return Axios.put(`/analyse-item/${item_id}/`, data, header);
    },
    retrieveItem(item_id) {
        return Axios.get(`/analyse-item/${item_id}/`, header);
    },
};

const AnalysisResults = {
    updateResult(result) {
        return Axios.put(`/analyse-result/${result.id}/`, result, header);
    },
};

const Sales = {
    getTypes() {
        return Axios.get(`/sales/types/`, header);
    },
    getStatuses() {
        return Axios.get(`/sales/statuses/`, header);
    },
    getSale(sale_id) {
        return Axios.get(`/sales/${sale_id}/`, header);
    },
    createSale(sale) {
        return Axios.post('/sales/', sale, header);
    },
    updateSale(sale) {
        return Axios.put(`/sales/${sale.id}/`, sale, header);
    },
    getQuoteUUID(sale_id) {
        return Axios.get(`/sales/get-quote-uuid/${sale_id}/`, header);
    },
    getQuote(uuid) {
        return Axios.get(`/quote/${uuid}/`, header);
    },
    enterPin(data, uuid) {
        return Axios.post(`/quote-enter/${uuid}/`, data, header);
    },
    sendMailToReceivers(quote_id, data) {
        return Axios.post(`/quote/send/${quote_id}/`, data, header);
    },
    revokeAccess(quote_id) {
        return Axios.post(`/quote/revoke/${quote_id}/`, header);
    },
    createTarget(target) {
        return Axios.post('/sales/target/', target, header);
    },
    updateTarget(target) {
        return Axios.put(`/sales/target/${target.id}/`, target, header);
    },
    deleteTarget(target) {
        return Axios.delete(`/sales/target/${target.id}/`, header);
    },
    acceptQuote(quote) {
        return Axios.put('/accept-quote/', quote, header);
    },
    rejectQuote(quote) {
        return Axios.put('/reject-quote/', quote, header);
    },
    delete(sale_id) {
        return Axios.delete(`/sales/${sale_id}/`);
    },
    fetchPDF(uuid) {
        return Axios.get(`/sales/download-quote/${uuid}/`, {
            responseType: 'blob',
        });
    },
    getQuoteIntro(id) {
        return Axios.get(`/sales/quote-intro/${id}/`, header);
    },
    createQuoteIntro(data) {
        return Axios.post(`/sales/quote-intro/`, data, header);
    },
    updateQuoteIntro(data) {
        return Axios.put(`/sales/quote-intro/${data.id}/`, data, header);
    },
    deleteQuoteIntro(id) {
        return Axios.delete(`/sales/quote-intro/${id}/`, header);
    },
    searchQuoteIntros(search) {
        return Axios.get(`/sales/quote-intro/search/?q=${search}`, header);
    },
    parseQuoteIntroText(quoteIntroId, sale) {
        return Axios.post(`/sales/quote-intro/${quoteIntroId}/parse-text/`, sale, header);
    },
    getQuoteTemplate(id) {
        return Axios.get(`/sales/quote-template/${id}/`, header);
    },
    createQuoteTemplate(data) {
        return Axios.post(`/sales/quote-template/`, data, header);
    },
    updateQuoteTemplate(data) {
        return Axios.put(`/sales/quote-template/${data.id}/`, data, header);
    },
    deleteQuoteTemplate(id) {
        return Axios.delete(`/sales/quote-template/${id}/`, header);
    },
};

const Source = {
    getSources() {
        return Axios.get('/sales/sources/');
    },
    getSource(source_id) {
        return Axios.get(`/sales/sources/${source_id}/`);
    },
    newSource(data) {
        return Axios.post('/sales/source/', data);
    },
    editSource(data) {
        return Axios.put(`/sales/source/${data.id}/`, data);
    },
};

const QuoteChat = {
    getMessages(quote_id) {
        return Axios.get(`/quote-chat/messages/${quote_id}/`, header);
    },
    markRead(messageId, email) {
        return Axios.post(`/quote-chat/mark-read/${messageId}/`, { read_by: email }, header);
    },
};

const Backend = {
    getMessages() {
        return Axios.get(`/messages/list/`);
    },
};

const Settings = {
    get(name) {
        return Axios.get(`/settings/${name}/`);
    },
    update(name, data) {
        return Axios.put(`/settings/${name}/`, data);
    },
    importSettingsFromExact() {
        return Axios.get(`/import-exact-settings/`);
    },
};

const AppSettings = {
    getDeletedReason(reason_id) {
        return Axios.get(`/app-settings/deleted-reasons/${reason_id}/`);
    },
    getDeletedReasons() {
        return Axios.get(`/app-settings/deleted-reasons/`);
    },
    createDeletedReason(reason) {
        return Axios.post('/app-settings/deleted-reasons/', reason);
    },
    updateDeletedReason(reason) {
        return Axios.put(`/app-settings/deleted-reasons/${reason.id}/`, reason);
    },
    deleteDeletedReason(reason_id) {
        return Axios.delete(`/app-settings/deleted-reasons/${reason_id}/`);
    },
    refreshAPIsStatus() {
        return Axios.post(`/api/apis-status/refresh/`);
    },
};

const Billing = {
    listUnpaidInvoices(search = '') {
        return Axios.get(`/billing/invoices/list/unpaid/?search=${search}`);
    },
    createInvoice(order_data, invoice_data, exact_id, force_create_relation = false, scope = 'debtor') {
        return Axios.post(`/billing/invoice/`, {
            order_data: order_data,
            invoice_data: invoice_data,
            exact_id: exact_id,
            force_create_relation: force_create_relation,
            scope: scope,
        });
    },
    createPeriodicInvoice(type) {
        return Axios.post(`/billing/create-periodic-invoice/${type}/`, {});
    },
    createInvoiceWithoutOrders(invoice_data, exact_id, force_create_relation = false, scope = 'debtor') {
        return Axios.post(`/billing/invoice/without-orders/`, {
            invoice_data: invoice_data,
            exact_id: exact_id,
            force_create_relation: force_create_relation,
            scope: scope,
        });
    },
    createCreditInvoice(invoice_id, data) {
        return Axios.post(`/billing/invoice/${invoice_id}/credit/`, data);
    },
    createDebitInvoice(invoice_id, data) {
        return Axios.post(`/billing/invoice/${invoice_id}/debit/`, data);
    },
    updateInvoice(order_data, invoice_data) {
        return Axios.put(`/billing/invoice/${invoice_data.id}/update/`, {
            order_data: order_data,
            invoice_data: invoice_data,
        });
    },
    sendInvoice(data, invoice_id) {
        return Axios.post(`/billing/invoice/${invoice_id}/send/`, data);
    },
    setPaid(invoice_id) {
        return Axios.patch(`/billing/invoice/${invoice_id}/paid/`);
    },
    setUnpaid(invoice_id) {
        return Axios.patch(`/billing/invoice/${invoice_id}/unpaid/`);
    },
    collectionOrderSetUnbillable(order_id, kwargs = {}) {
        return Axios.patch(`/collection-orders/${order_id}/unbillable/`, kwargs);
    },
    deliveryOrderSetUnbillable(order_id, kwargs = {}) {
        return Axios.patch(`/delivery-orders/${order_id}/unbillable/`, kwargs);
    },
    collectionOrderSetReviewRequired(order_id, data) {
        return Axios.patch(`/collection-orders/${order_id}/review-required/`, data);
    },
    deliveryOrderSetReviewRequired(order_id, data) {
        return Axios.patch(`/delivery-orders/${order_id}/review-required/`, data);
    },
    outboundOrderSetReviewRequired(order_id, data) {
        return Axios.patch(`/outbound-orders/${order_id}/review-required/`, data);
    },
    outsourcedOrderSetReviewRequired(order_id, data) {
        return Axios.patch(`/outsourced-orders/${order_id}/review-required/`, data);
    },
    getInvoice(invoice_id, fields = '') {
        return Axios.get(`/billing/invoice/${invoice_id}/?${fields}`);
    },
    getSentLog(invoice_id) {
        return Axios.get(`/billing/invoice/${invoice_id}/sent-log/`);
    },
    generateInvoicePDF(invoice_id) {
        return Axios.get(`/billing/invoice/${invoice_id}/generate-pdf/`);
    },
    previewInvoicePDF(order_data, invoice_data) {
        return Axios.post(
            `/billing/invoice/preview-pdf/`,
            {
                order_data: order_data,
                invoice_data: invoice_data,
            },
            { responseType: 'blob' }
        );
    },
    resendSendTask(id) {
        return Axios.post(`/billing/send-task/${id}/resend/`);
    },
    getInvoicesFacts(relation_id) {
        return Axios.get(`/billing/invoices/${relation_id}/unpaid/`);
    },
    updateReference(invoice) {
        return Axios.patch(`/billing/invoice/${invoice.id}/change-reference/`, {
            invoice: invoice,
        });
    },
    updateReminder(invoice) {
        return Axios.patch(`/billing/invoice/${invoice.id}/change-reminder/`, {
            invoice: invoice,
        });
    },
    updateNote(invoice) {
        return Axios.patch(`/billing/invoice/${invoice.id}/update-note/`, {
            invoice: invoice,
        });
    },
    charts(ident, table, filters) {
        return Axios.get(`/billing/charts/${table}/${ident}/?${filters}`);
    },
    chartsReadyForBilling(ident, filters, config) {
        return Axios.get(`/billing/charts-ready-for-billing/${ident}/?${filters}`, config);
    },
    chartsAwaitingInvoice(ident, filters, config) {
        return Axios.get(`/billing/charts-awaiting-invoice/${ident}/?${filters}`, config);
    },
    chartsReview(ident, filters, config) {
        return Axios.get(`/billing/charts-review/${ident}/?${filters}`, config);
    },
    setOrderToReadyForBilling(invoice_id, orderType, data) {
        return Axios.patch(`/billing/invoice/${invoice_id}/reopen-order/${orderType}/`, data);
    },
    getInvoiceStyle() {
        return Axios.get(`/billing/invoice/style/`);
    },
    updateInvoiceStyle(obj) {
        return Axios.post(`/billing/invoice/style/${obj.id}/update/`);
    },
    removeInvoiceStyle(document_id) {
        return Axios.post(`/billing/invoice/style/${document_id}/remove/`);
    },
    sendPaymentRequest(order_id, order_type) {
        return Axios.post(`/billing/order/${order_id}/${order_type}/send-payment-request/`);
    },
    fetchOrdersForPurchaseInvoiceConnection(debtor_id) {
        return Axios.get(`/orders/awaiting-invoice/${debtor_id}/`);
    },
    fetchPurchaseInvoicesFromExact(debtor_id) {
        return Axios.get(`/billing/exact-purchase-invoices/${debtor_id}/`);
    },
    createAndConnectPurchaseInvoice(data) {
        return Axios.post(`/billing/create-and-connect-purchase-invoice/`, data);
    },
    fetchPurchaseInvoiceAttachment(document_uuid) {
        return Axios.get(`/billing/fetch-purchase-invoice-attachment/${document_uuid}/`);
    },
    fetchInvoiceOrders(invoice_id) {
        return Axios.get(`/billing/invoices/orders/${invoice_id}/`);
    },
    fetchPurchaseInvoiceOrders(invoice_id) {
        return Axios.get(`/billing/purchase-invoices/orders/${invoice_id}/`);
    },
    fetchPurchaseInvoiceDocument(invoice_id) {
        return Axios.get(`/billing/purchase-invoices/${invoice_id}/document/`);
    },
    disconnectAndRemovePurchaseInvoice(id) {
        return Axios.delete(`/billing/delete-and-disconnect-purchase-invoice/${id}/`);
    },
    fetchOrdersWithDebtor(order_id, order_type) {
        return Axios.get(`/billing/order/${order_id}/${order_type}/with-debtor/`);
    },
    purchaseInvoiceEdit(data) {
        return Axios.post(`/billing/edit-purchase-invoice/${data.id}/`, data);
    },
    fetchReminders(invoice_id) {
        return Axios.get(`/billing/invoice/${invoice_id}/reminders/`);
    },
};

const Kanban = {
    getKanban(module, q_params = '') {
        return Axios.get(`/kanban/get-kanban/${module}/${q_params}`);
    },
    getExtra(module) {
        return Axios.get(`/kanban/get-extra/${module}/`);
    },
    updateElementsOrder(module, elements) {
        return Axios.put(`/kanban/update-kanban-orders/${module}/`, elements);
    },
    loadBoardData(module, board, q_params = '') {
        return Axios.get(`/kanban/load-board-data/${module}/${board.id}/${q_params}`);
    },
};

const ManagementInformation = {
    chartsReporting(ident, filters, config) {
        return Axios.get(`/management-information/reporting/charts/${ident}/?${filters}`, config);
    },
    factsReporting(filters, upd_now = false, config) {
        return Axios.get(`/management-information/reporting/facts/?${filters}&update-now=${upd_now}`, config);
    },
    open_orders() {
        return Axios.get('/management-information/operations/open-orders/');
    },
    regions_distribution(daterange) {
        return Axios.get(
            `/management-information/operations/regions-distribution/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    weight_hours_distances(daterange) {
        return Axios.get(
            `/management-information/operations/weight-hours-distances/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    employee_activity(daterange) {
        return Axios.get(
            `/management-information/operations/employee-activity/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    system_activity(daterange) {
        return Axios.get(
            `/management-information/operations/system-activity/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    waste_groups_graph(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers) {
            if (drivers.length > 0) {
                drivers_get_params = '';
            }
            for (let driver of drivers) {
                drivers_get_params += `&drivers_ids=${driver.id}`;
            }
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/graph/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    average_collection_weight(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers) {
            if (drivers.length > 0) {
                drivers_get_params = '';
            }
            for (let driver of drivers) {
                drivers_get_params += `&drivers_ids=${driver.id}`;
            }
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/average-collection-weight/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    total_collected(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers) {
            if (drivers.length > 0) {
                drivers_get_params = '';
            }
            for (let driver of drivers) {
                drivers_get_params += `&drivers_ids=${driver.id}`;
            }
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/total-collected/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    average_execution(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers.length > 0) {
            drivers_get_params = '';
        }
        for (let driver of drivers) {
            drivers_get_params += `&drivers_ids=${driver.id}`;
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/average-execution/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    top_five_disposers(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers) {
            if (drivers.length > 0) {
                drivers_get_params = '';
            }
            for (let driver of drivers) {
                drivers_get_params += `&drivers_ids=${driver.id}`;
            }
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/top-five-disposers/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    top_five_receivers(waste_group, drivers, years, order_type = 'collection') {
        let drivers_get_params = '&drivers_ids=all';
        if (drivers) {
            if (drivers.length > 0) {
                drivers_get_params = '';
            }
            for (let driver of drivers) {
                drivers_get_params += `&drivers_ids=${driver.id}`;
            }
        }
        let years_get_params = '';
        for (let year of years) {
            years_get_params += `&years=${year}`;
        }
        return Axios.get(
            `/management-information/waste-groups/top-five-receivers/?waste_group_id=${waste_group.id}${drivers_get_params}${years_get_params}&order_type=${order_type}`
        );
    },
    finance_exact() {
        return Axios.get('/management-information/finance/exact/');
    },
    get_expected_turnover() {
        return Axios.get('/management-information/finance/get-expected-turnover/');
    },
    get_top_drivers(daterange) {
        return Axios.get(
            `/management-information/finance/get-top-drivers/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    get_top_relation_debtors_card(daterange) {
        return Axios.get(
            `/management-information/finance/get-top-relation-debtors-card/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    get_average_price_per_ton_card(daterange) {
        return Axios.get(
            `/management-information/finance/get-average-price-per-ton-card/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
    get_time_between_execution_and_invoicing(daterange) {
        return Axios.get(
            `/management-information/finance/get-time-between-execution-and-invoicing-card/?start_date=${daterange[0]}&end_date=${daterange[1]}`
        );
    },
};

const Weighing = {
    get(weighing_id) {
        return Axios.get(`/storage/weighing/${weighing_id}/`);
    },
    list() {
        return Axios.get(`/storage/weighing/`);
    },
    getWithNoMutations(eural_code = '', storage_waste_group_required = false) {
        return Axios.get(
            `/storage/weighing/no-mutation/?eural_code=${eural_code}&storage_waste_group_required=${storage_waste_group_required}`
        );
    },
    getForTransportUnits(transport_units) {
        return Axios.get(`/storage/weighing/by-transport-units/?${transport_units}`, header);
    },
    getForCollectionWasteGroup(collection_waste_groups) {
        return Axios.get(`/storage/weighing/by-collection-waste-groups/?${collection_waste_groups}`, header);
    },
    getForBulk(bulk_id, bulk_type) {
        return Axios.get(`/storage/weighing/for-bulk-mutations/?bulk_id=${bulk_id}&bulk_type=${bulk_type}`, header);
    },
    getFree() {
        return Axios.get(`/storage/weighing/free/`, header);
    },
    create(data) {
        return Axios.post(`/storage/weighing/`, data);
    },
    update(data) {
        return Axios.put(`/storage/weighing/${data.id}/`, data);
    },
    delete(weighing_id, reason) {
        return Axios.delete(`/storage/weighing/${weighing_id}/`, {
            data: {
                reason: reason,
            },
        });
    },
    connect(data) {
        return Axios.put(`/storage/weighing/connect/`, data, header);
    },
    disconnect(data) {
        return Axios.put(`/storage/weighing/disconnect/`, data, header);
    },
    printWeighing(weighing_id) {
        return Axios.get(`/storage/weighing/${weighing_id}/print-label/`, header);
    },
};

const AlternativeDebtor = {
    retrieve(id) {
        return Axios.get(`/alternative-debtor/${id}/`);
    },
    create(alternative_debtor) {
        return Axios.post(`/alternative-debtor/save/`, alternative_debtor);
    },
    update(alternative_debtor) {
        return Axios.put(`/alternative-debtor/${alternative_debtor.id}/`, alternative_debtor);
    },
    delete(alternative_debtor) {
        return Axios.delete(`/alternative-debtor/${alternative_debtor.id}/`);
    },
};

const Amice = {
    getLog(order_id, order_type) {
        return Axios.get(`/amice/log/${order_id}/${order_type}/`);
    },
    report(order_id, order_type) {
        return Axios.post('/amice/report/', {
            order_id: order_id,
            order_type: order_type,
        });
    },
    editReport(data) {
        return Axios.post('/amice/edit-report/', data);
    },
    revert(order_id, order_type) {
        return Axios.post('/amice/revert/', {
            order_id: order_id,
            order_type: order_type,
        });
    },
    batchReport(orders) {
        return Axios.post('/amice/batch-report/', { orders: orders });
    },
    batchReportStatus() {
        return Axios.get('/amice/batch-report/status/');
    },
    getWasteStreamComparison(
        year,
        only_difference = false,
        search = '',
        establishment_id = '',
        page = 1,
        page_size = 25
    ) {
        return Axios.get('/amice/waste-stream-comparison/', {
            params: {
                year,
                only_difference,
                search,
                establishment_id: establishment_id || undefined,
                page,
                page_size,
            },
        });
    },
    getFilterOptions() {
        return Axios.get('/amice/filter-options/');
    },
};

const Matis = {
    getLog(order_id, order_type) {
        return Axios.get(`/matis/log/${order_id}/${order_type}/`);
    },
    report(order_id, order_type) {
        return Axios.post('/matis/report/', {
            order_id: order_id,
            order_type: order_type,
        });
    },
};

const globalSearch = {
    search(search, options) {
        return Axios.get(`/app-settings/global-search/?q=${encodeURIComponent(search)}`, options);
    },
};

const ScannedDocuments = {
    delete(id) {
        return Axios.delete(`/scans/delete/${id}/`);
    },
};

const EVOA = {
    create(data) {
        return Axios.post(`/app-settings/evoa/license/`, data);
    },
    update(data) {
        return Axios.put(`/app-settings/evoa/license/${data.id}/`, data);
    },
    invalidate(evoa_license_id) {
        return Axios.put(`/app-settings/evoa/license/${evoa_license_id}/invalidate/`);
    },
    delete(evoa_license_id) {
        return Axios.delete(`/app-settings/evoa/license/${evoa_license_id}/`);
    },
    fetch_license(data) {
        return Axios.post(`/app-settings/evoa/license/find/`, data);
    },
    get_license(id) {
        return Axios.get(`/app-settings/evoa/license/${id}/`);
    },
};

const KeyFobs = {
    fetch() {
        return Axios.get('/app-settings/keyfobs/');
    },
    create(data) {
        return Axios.post('/app-settings/keyfobs/', data);
    },
    update(data) {
        return Axios.put(`/app-settings/keyfobs/${data.id}/`, data);
    },
    delete(id) {
        return Axios.delete(`/app-settings/keyfobs/${id}/`);
    },
};

const Payments = {
    BUNQ: {
        ia_api_key_set() {
            return Axios.get('/bunq/settings/api-key/');
        },
        get(params) {
            return Axios.get(`/bunq/payments/get-payments/?${params}`);
        },
        get_payment(id, params) {
            return Axios.get(`/bunq/payments/get-payment/${id}/?${params}`);
        },
        get_accounts() {
            return Axios.get('/bunq/accounts/get-accounts/');
        },
        get_monetary_accounts() {
            return Axios.get('/bunq/monetary-accounts/get-accounts/');
        },
        renew_monetary_accounts() {
            return Axios.put('/bunq/monetary-accounts/renew/');
        },
        activate_monetary_account(id) {
            return Axios.put(`/bunq/monetary-accounts/activate/${id}/`);
        },
        get_active_monetary_account() {
            return Axios.get(`/bunq/monetary-account/active/`);
        },
        execute_scheduled_payment(id) {
            return Axios.post(`/bunq/payments/execute-scheduled-payment/${id}/`);
        },
        get_requisite_types() {
            return Axios.get('/bunq/accounts/get-requisite-types/');
        },
        make(data) {
            return Axios.post('/bunq/payments/make-payment/', data);
        },
        edit_payment(data) {
            return Axios.put(`/bunq/payments/${data.id}/`, data);
        },
        cancel_payment(id) {
            return Axios.delete(`/bunq/payments/${id}/`);
        },
        create_account(data) {
            return Axios.post('/bunq/accounts/create-account/', data);
        },
        get_cash_flow(year) {
            return Axios.get(`/bunq/payments/cash-flow/${year}/`);
        },
        get_per_week_number(year, week_number) {
            return Axios.get(`/bunq/payments/payments-per-week-number/${year}/${week_number}/`);
        },
    },
};

const Predictions = {
    deactivateCustomer(id, data) {
        return Axios.post(`/forecast-customer/${id}/deactivate/`, data);
    },
    activateCustomer(id, data) {
        return Axios.post(`/forecast-customer/${id}/activate/`, data);
    },
};

const EventLog = {
    modules(search) {
        return Axios.get(`/event-logging/modules/?search=${search}`);
    },
    models(search) {
        return Axios.get(`/event-logging/models/?search=${search}`);
    },
    eventCodes(search) {
        return Axios.get(`/event-logging/event-codes/?search=${search}`);
    },
};

const IOP = {
    getOrderPrefill(lineId, orderType) {
        return Axios.get(`/iop/api/review/lines/${lineId}/order-prefill/?order_type=${orderType}`);
    },
    linkLine(lineId, payload) {
        return Axios.post(`/iop/api/review/lines/${lineId}/link/`, payload, header);
    },
};

export default {
    App: App,
    User: User,
    UserGroup: UserGroup,
    Group: Group,
    PermissionGroup: PermissionGroup,
    Axios: Axios,
    Address: Address,
    Contact: Contact,
    ContactRelation: ContactRelation,
    Document: Document,
    Planning: Planning,
    Relation: Relation,
    Kruispunt: Kruispunt,
    Tag: Tag,
    Establishment: Establishment,
    ProcessingMethod: ProcessingMethod,
    OutboundProcessingMethod: OutboundProcessingMethod,
    Institute: Institute,
    Industry: Industry,
    Employee: Employee,
    TimeRegistration: TimeRegistration,
    Storage: Storage,
    StorageHistory: StorageHistory,
    Collection: Collection,
    Task: Task,
    Notification: Notification,
    EuralCodes: EuralCodes,
    PhysicalProperties: PhysicalProperties,
    PackagingTypes: PackagingTypes,
    Transport: Transport,
    Csrftoken: csrftoken,
    WasteStream: WasteStream,
    Driver: Driver,
    Region: Region,
    CollectionWasteGroup: CollectionWasteGroup,
    TransportNumber: TransportNumber,
    Categories: Categories,
    Language: Language,
    CollectionOrder: CollectionOrder,
    DeliveryOrder: DeliveryOrder,
    InternalOrder: InternalOrder,
    OutboundOrder: OutboundOrder,
    OutsourcedOrder: OutsourcedOrder,
    PreflightQueue: PreflightQueue,
    Catalog: Catalog,
    Holidays: Holidays,
    Sensor: Sensor,
    Timeline: Timeline,
    TimelineNote: TimelineNote,
    Stock: Stock,
    Bulks: Bulks,
    AnalysisItems: AnalysisItems,
    AnalysisResults: AnalysisResults,
    Settings: Settings,
    Sales: Sales,
    QuoteChat: QuoteChat,
    Kanban: Kanban,
    Billing: Billing,
    Source: Source,
    ManagementInformation: ManagementInformation,
    Weighing: Weighing,
    AlternativeDebtor: AlternativeDebtor,
    Backend: Backend,
    Amice: Amice,
    globalSearch: globalSearch,
    CollectionOrderTemplate: CollectionOrderTemplate,
    OutsourcedOrderTemplate: OutsourcedOrderTemplate,
    OutsourcedRegion: OutsourcedRegion,
    OutboundOrderTemplate: OutboundOrderTemplate,
    InternalOrderTemplate: InternalOrderTemplate,
    Orders: Orders,
    StorageWasteGroup: StorageWasteGroup,
    Mail: Mail,
    kvkNL: kvkNL,
    handelsregisterDE: handelsregisterDE,
    EVOA: EVOA,
    ScannedDocuments: ScannedDocuments,
    KeyFobs: KeyFobs,
    Payments: Payments,
    Predictions: Predictions,
    EventLog: EventLog,
    Matis: Matis,
    AppSettings: AppSettings,
    TransportUnit: TransportUnit,
    IOP: IOP,
};
